import { eventId } from "@shared/analytics/walletEvents";
import type { PublishedTermsDescriptor, YaasStatsResponse } from "@shared/types/yaas";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import type { EnrollmentJourney } from "@/services/analytics/enrollment";

import { platformStatsQuery } from "./platformStatsQuery";

export async function verifyEnrollmentTerms(stats: YaasStatsResponse): Promise<PublishedTermsDescriptor> {
    const program = stats.retailProgram;
    const terms = program?.enrollmentTerms;
    if (
        stats.retailTermsStatus !== "available" ||
        !terms?.name ||
        !terms.version ||
        !terms.documentKey ||
        typeof terms.content !== "string" ||
        terms.contentFormat !== "text/markdown; charset=utf-8" ||
        !/^sha256:[a-f0-9]{64}$/.test(terms.contentDigest)
    )
        throw new Error("Terms are unavailable. Refresh to try again.");
    if (
        program?.termsRequirement &&
        (program.termsRequirement.terms.length !== 1 ||
            program.termsRequirement.terms[0]?.termsUuid !== terms.termsUuid ||
            program.termsRequirement.terms[0]?.contentDigest !== terms.contentDigest)
    )
        throw new Error("This program needs a different enrollment flow.");
    const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(terms.content));
    const digest = `sha256:${Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
    if (digest !== terms.contentDigest) throw new Error("Terms content could not be verified. Refresh to try again.");
    return Object.freeze({ ...terms });
}

export function useRetailTerms(analytics?: EnrollmentJourney): {
    terms: PublishedTermsDescriptor | null;
    error: string | null;
    isLoading: boolean;
    refresh: () => Promise<void>;
} {
    const check = useRef<{ id: string; started: number; settled: boolean; revision?: string } | null>(null);
    const refreshing = useRef(false);
    const stats = useQuery(platformStatsQuery);
    const verified = useQuery({
        queryKey: ["yaas", "enrollment-terms", stats.data?.retailProgram, stats.data?.retailTermsStatus],
        queryFn: () => verifyEnrollmentTerms(stats.data!),
        enabled: !!stats.data,
        retry: false
    });
    const revision = verified.data?.contentDigest;
    const startCheck = useCallback(
        (trigger: string): void => {
            check.current = { id: eventId(), started: performance.now(), settled: false, revision };
            analytics?.send("enroll:check_started", { check_id: check.current.id, check_type: "terms", trigger });
        },
        [analytics, revision]
    );
    const finishCheck = useCallback(
        (failed: boolean, version?: string): void => {
            const active = check.current;
            if (!active || active.settled) return;
            active.settled = true;
            active.revision = revision;
            analytics?.send(failed ? "enroll:check_failed" : "enroll:check_completed", {
                check_id: active.id,
                check_type: "terms",
                result: failed ? undefined : "loaded",
                error_code: failed ? "terms_check_failed" : undefined,
                terms_version: version,
                duration_ms: performance.now() - active.started
            });
        },
        [analytics, revision]
    );
    useEffect(() => {
        if (!analytics || refreshing.current) return;
        if (!check.current || (check.current.settled && revision !== check.current.revision))
            startCheck(check.current ? "refresh" : "initial");
        if (stats.isFetching || verified.isFetching) return;
        if (stats.isError || verified.isError) finishCheck(true);
        else if (verified.data) finishCheck(false, verified.data.version);
    }, [
        analytics,
        stats.isFetching,
        stats.isError,
        verified.isFetching,
        verified.isError,
        verified.data,
        revision,
        startCheck,
        finishCheck
    ]);
    return {
        terms: stats.isError ? null : (verified.data ?? null),
        error: stats.error?.message ?? verified.error?.message ?? null,
        isLoading: stats.isLoading || verified.isLoading,
        refresh: async (): Promise<void> => {
            refreshing.current = true;
            startCheck("refresh");
            try {
                const latestStats = await stats.refetch();
                const latestTerms = await verified.refetch();
                finishCheck(latestStats.isError || latestTerms.isError, latestTerms.data?.version);
            } finally {
                refreshing.current = false;
            }
        }
    };
}
